/**
 * Clinician "Reports inbox" — the doctor's review queue for parent-submitted
 * reports (the unified intake).
 *
 * For each submission the doctor:
 *   1. Analyzes it (POST /intake/submissions/:id/analyze) → the server classifies
 *      the report and returns an AI-extracted DRAFT for the right type.
 *   2. Reviews/edits the draft on the appropriate inline form (NFS analyte table,
 *      serology marker table, imaging findings, or an "other" notice).
 *   3. Files it into the right clinical table (labs / serology / appointments),
 *      passing sourceAttachmentId so the original scan stays linked.
 *   4. The submission is then marked reviewed (POST .../reviewed) — or dismissed.
 *
 * The AI output is ALWAYS a draft the clinician verifies before filing. A "View
 * scan" link lets the doctor check the source document at any point.
 *
 * App-specific labels that are not in the shared i18n catalog use a tiny inline
 * FR/EN map keyed off the current locale.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ANALYTE_KEYS,
  ANALYTE_UNITS,
  EXAM_TYPES,
  APPOINTMENT_STATUSES,
  SEROLOGY_MARKERS,
  SEROLOGY_QUALITATIVE,
  type AnalyteKey,
  type AnalyteUnit,
  type SerologyMarker,
} from '@toxofollow/shared';
import { api, downloadFile } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Button, Badge, Field, inputClass, Table } from '../components/ui.js';
import { Select, Textarea, ErrorState, QueryState } from '../components/form.js';

// --- Inline bilingual strings (not present in the shared catalog) -----------
const L = {
  title: { fr: 'Comptes rendus à analyser', en: 'Reports inbox' },
  intro: {
    fr: 'Comptes rendus envoyés par les parents. Analysez chacun, vérifiez le brouillon, puis classez-le.',
    en: 'Reports sent by parents. Analyze each, verify the draft, then file it.',
  },
  filename: { fr: 'Fichier', en: 'File' },
  note: { fr: 'Note du parent', en: 'Parent note' },
  from: { fr: 'Envoyé par', en: 'Submitted by' },
  date: { fr: 'Date', en: 'Date' },
  statusCol: { fr: 'Statut', en: 'Status' },
  actions: { fr: 'Actions', en: 'Actions' },
  viewScan: { fr: 'Voir le document', en: 'View scan' },
  analyze: { fr: 'Analyser et classer', en: 'Analyze & file' },
  analyzing: { fr: 'Analyse en cours…', en: 'Analyzing…' },
  dismiss: { fr: 'Écarter', en: 'Dismiss' },
  cancel: { fr: 'Annuler', en: 'Cancel' },
  statusPending: { fr: 'En attente', en: 'Pending' },
  statusReviewed: { fr: 'Examiné', en: 'Reviewed' },
  statusDismissed: { fr: 'Écarté', en: 'Dismissed' },
  empty: { fr: 'Aucun compte rendu à analyser.', en: 'No reports to review.' },
  draftNotice: { fr: 'Brouillon — à vérifier avant de classer (IA)', en: 'Draft — verify before filing (AI)' },
  draftHint: {
    fr: 'Les valeurs ci-dessous sont un brouillon issu du document. Corrigez-les puis vérifiez le document source avant de classer.',
    en: 'The values below are a draft from the document. Correct them and check the source scan before filing.',
  },
  detectedNfs: { fr: 'Détecté : NFS / Labo', en: 'Detected: CBC / Lab' },
  detectedSerology: { fr: 'Détecté : Sérologie', en: 'Detected: Serology' },
  detectedImaging: { fr: 'Détecté : Imagerie', en: 'Detected: Imaging' },
  detectedOther: { fr: 'Type non reconnu', en: 'Unrecognized type' },
  otherNotice: {
    fr: 'Compte rendu clinique non reconnu — classez-le manuellement ou écartez-le.',
    en: 'Not a recognized clinical report — file it manually or dismiss it.',
  },
  markReviewed: { fr: 'Marquer comme examiné', en: 'Mark reviewed' },
  fileNfs: { fr: 'Classer dans NFS / Labos', en: 'File into NFS / Labs' },
  fileSerology: { fr: 'Classer dans Sérologie', en: 'File into Serology' },
  fileImaging: { fr: 'Classer dans RDV / Examens', en: 'File into Appointments' },
  filed: { fr: 'Classé et marqué comme examiné', en: 'Filed and marked reviewed' },
  // form fields
  sampleDate: { fr: 'Date de prélèvement', en: 'Sample date' },
  resultDate: { fr: 'Date du résultat', en: 'Result date' },
  analyte: { fr: 'Analyte', en: 'Analyte' },
  customLabel: { fr: 'Libellé du rapport', en: 'Label from report' },
  customUnit: { fr: 'Unité du rapport', en: 'Unit from report' },
  value: { fr: 'Valeur', en: 'Value' },
  unit: { fr: 'Unité', en: 'Unit' },
  refLow: { fr: 'Réf. basse', en: 'Ref low' },
  refHigh: { fr: 'Réf. haute', en: 'Ref high' },
  addAnalyte: { fr: '+ Ajouter un analyte', en: '+ Add analyte' },
  addMarker: { fr: '+ Ajouter un marqueur', en: '+ Add marker' },
  remove: { fr: 'Retirer', en: 'Remove' },
  marker: { fr: 'Marqueur', en: 'Marker' },
  qualitative: { fr: 'Qualitatif', en: 'Qualitative' },
  titer: { fr: 'Titre', en: 'Titer' },
  titerUnit: { fr: 'Unité', en: 'Unit' },
  interpretation: { fr: 'Interprétation / avidité', en: 'Interpretation / avidity' },
  none: { fr: '(aucun)', en: '(none)' },
  examType: { fr: 'Type d’examen', en: 'Exam type' },
  examDate: { fr: 'Date de l’examen', en: 'Exam date' },
  specialist: { fr: 'Spécialiste / centre', en: 'Specialist / facility' },
  findings: { fr: 'Conclusions', en: 'Findings' },
  findingsPlaceholder: {
    fr: 'Ex. ventricules de taille normale, pas de microcalcification…',
    en: 'E.g. normal ventricle size, no microcalcification…',
  },
  status: { fr: 'Statut', en: 'Status' },
  nextDate: { fr: 'Prochain examen', en: 'Next exam' },
  g6pdDetected: { fr: 'G6PD détecté sur le rapport', en: 'G6PD detected on the report' },
  applyToRecord: { fr: 'Appliquer au dossier du patient', en: 'Apply to the patient record' },
} as const;

const G6PD_LABEL: Record<string, { fr: string; en: string }> = {
  normal: { fr: 'normal', en: 'normal' },
  abnormal: { fr: 'anormal (déficit)', en: 'abnormal (deficient)' },
  pending: { fr: 'en attente', en: 'pending' },
  not_applicable: { fr: 'non applicable', en: 'not applicable' },
  unknown: { fr: 'inconnu', en: 'unknown' },
};

type Lang = 'fr' | 'en';
type Status = 'pending' | 'reviewed' | 'dismissed';

interface Submission {
  id: string;
  note: string | null;
  status: Status;
  detectedType: string | null;
  submittedByRole: string;
  resultType: string | null;
  createdAt: string;
  reviewedAt: string | null;
  attachmentId: string;
  filename: string;
  contentType: string;
}

// --- Analyze response shapes ------------------------------------------------
interface NfsSuggestion {
  key: AnalyteKey;
  customLabel?: string;
  value: string;
  unit: AnalyteUnit;
  customUnit?: string;
  referenceLow?: string;
  referenceHigh?: string;
  confidence: string;
}
interface NfsDraft {
  extracted: boolean;
  suggestions: NfsSuggestion[];
  sampleDateGuess?: string;
  baseline?: { g6pdStatus?: string };
}
interface SerologySuggestion {
  marker: SerologyMarker;
  qualitative?: string;
  titer?: string;
  titerUnit?: string;
  interpretation?: string;
}
interface SerologyDraft {
  extracted: boolean;
  results: SerologySuggestion[];
  sampleDateGuess?: string;
}
interface ImagingDraft {
  summarized: boolean;
  examType?: string;
  examDate?: string;
  specialist?: string;
  findings?: string;
  nextDate?: string;
}
type AnalyzeResponse =
  | { type: 'nfs'; attachmentId: string; draft: NfsDraft }
  | { type: 'serology'; attachmentId: string; draft: SerologyDraft }
  | { type: 'imaging'; attachmentId: string; draft: ImagingDraft }
  | { type: 'other'; attachmentId: string; draft: null };

const STATUS_META: Record<Status, { tone: 'info' | 'ok' | 'warn'; label: { fr: string; en: string } }> = {
  pending: { tone: 'info', label: L.statusPending },
  reviewed: { tone: 'ok', label: L.statusReviewed },
  dismissed: { tone: 'warn', label: L.statusDismissed },
};

const today = () => new Date().toISOString().slice(0, 10);
const fmtUnit = (u: string) => u.replace('per_mm3', '/mm³');

// Keys to invalidate after filing into a clinical table.
const INVALIDATE_KEYS = [['labs'], ['serology'], ['appointments'], ['surveillance'], ['alerts'], ['intake']];

export function ReportsInbox() {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const lang: Lang = locale === 'en' ? 'en' : 'fr';
  const tr = (m: { fr: string; en: string }) => m[lang];

  const list = useQuery({
    queryKey: ['intake'],
    queryFn: () => api.get<{ submissions: Submission[] }>('/intake/submissions'),
  });

  // Pending first, then by date (newest first) within each status bucket.
  const submissions = [...(list.data?.submissions ?? [])].sort((a, b) => {
    if (a.status !== b.status) return a.status === 'pending' ? -1 : b.status === 'pending' ? 1 : 0;
    return (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
  });

  function invalidateAll() {
    for (const key of INVALIDATE_KEYS) void qc.invalidateQueries({ queryKey: key });
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-800 mb-1">{tr(L.title)}</h1>
      <p className="text-sm text-slate-500 mb-4">{tr(L.intro)}</p>

      <Card title="">
        <QueryState
          isLoading={list.isLoading}
          isError={list.isError}
          error={list.error}
          errorFallback={t('common.error')}
          isEmpty={submissions.length === 0}
          emptyMessage={tr(L.empty)}
        >
          <div className="space-y-3">
            {submissions.map((s) => (
              <SubmissionRow key={s.id} submission={s} tr={tr} t={t} onFiled={invalidateAll} />
            ))}
          </div>
        </QueryState>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------

function SubmissionRow({
  submission: s,
  tr,
  t,
  onFiled,
}: {
  submission: Submission;
  tr: (m: { fr: string; en: string }) => string;
  t: (key: string) => string;
  onFiled: () => void;
}) {
  const qc = useQueryClient();
  const [analysis, setAnalysis] = useState<AnalyzeResponse | null>(null);
  const meta = STATUS_META[s.status];

  const downloadM = useMutation({
    mutationFn: () => downloadFile(`/attachments/${s.attachmentId}/download`, s.filename),
  });
  const analyzeM = useMutation({
    mutationFn: () => api.post<AnalyzeResponse>(`/intake/submissions/${s.id}/analyze`, {}),
    onSuccess: (res) => setAnalysis(res),
  });
  const dismissM = useMutation({
    mutationFn: () => api.post(`/intake/submissions/${s.id}/dismiss`, {}),
    onSuccess: () => {
      setAnalysis(null);
      void qc.invalidateQueries({ queryKey: ['intake'] });
    },
  });

  function afterFiled() {
    setAnalysis(null);
    onFiled();
    void qc.invalidateQueries({ queryKey: ['intake'] });
  }

  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="flex flex-wrap items-start gap-2">
        <Badge tone={meta.tone}>{tr(meta.label)}</Badge>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-800 truncate">{s.filename}</div>
          {s.note?.trim() && <div className="text-sm text-slate-600">{s.note.trim()}</div>}
          <div className="text-xs text-slate-400">
            {tr(L.from)}: {s.submittedByRole} · {s.createdAt?.slice(0, 10)}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 whitespace-nowrap">
          <button type="button" className="text-xs underline text-brand" onClick={() => downloadM.mutate()}>
            {tr(L.viewScan)}
          </button>
          {s.status === 'pending' && !analysis && (
            <>
              <Button type="button" onClick={() => analyzeM.mutate()} disabled={analyzeM.isPending}>
                {analyzeM.isPending ? tr(L.analyzing) : tr(L.analyze)}
              </Button>
              <Button type="button" variant="secondary" onClick={() => dismissM.mutate()} disabled={dismissM.isPending}>
                {tr(L.dismiss)}
              </Button>
            </>
          )}
          {analysis && (
            <Button type="button" variant="ghost" onClick={() => setAnalysis(null)}>
              {tr(L.cancel)}
            </Button>
          )}
        </div>
      </div>

      {downloadM.isError && <div className="mt-2"><ErrorState error={downloadM.error} fallback={t('common.error')} /></div>}
      {analyzeM.isError && <div className="mt-2"><ErrorState error={analyzeM.error} fallback={t('common.error')} /></div>}
      {dismissM.isError && <div className="mt-2"><ErrorState error={dismissM.error} fallback={t('common.error')} /></div>}

      {analysis && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <ReviewForm
            submission={s}
            analysis={analysis}
            tr={tr}
            t={t}
            onFiled={afterFiled}
            onViewScan={() => downloadM.mutate()}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function ReviewForm({
  submission: s,
  analysis,
  tr,
  t,
  onFiled,
  onViewScan,
}: {
  submission: Submission;
  analysis: AnalyzeResponse;
  tr: (m: { fr: string; en: string }) => string;
  t: (key: string) => string;
  onFiled: () => void;
  onViewScan: () => void;
}) {
  const detectedLabel =
    analysis.type === 'nfs'
      ? L.detectedNfs
      : analysis.type === 'serology'
      ? L.detectedSerology
      : analysis.type === 'imaging'
      ? L.detectedImaging
      : L.detectedOther;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Badge tone="info">{tr(detectedLabel)}</Badge>
        <button type="button" className="text-xs underline text-brand" onClick={onViewScan}>
          {tr(L.viewScan)}
        </button>
      </div>
      <div className="text-sm rounded-lg px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800">
        <strong>{tr(L.draftNotice)}</strong>
        <span className="block text-amber-700">{tr(L.draftHint)}</span>
      </div>

      {analysis.type === 'nfs' && (
        <NfsForm submission={s} draft={analysis.draft} tr={tr} t={t} onFiled={onFiled} />
      )}
      {analysis.type === 'serology' && (
        <SerologyForm submission={s} draft={analysis.draft} tr={tr} t={t} onFiled={onFiled} />
      )}
      {analysis.type === 'imaging' && (
        <ImagingForm submission={s} draft={analysis.draft} tr={tr} t={t} onFiled={onFiled} />
      )}
      {analysis.type === 'other' && <OtherForm submission={s} tr={tr} t={t} onFiled={onFiled} />}
    </div>
  );
}

// --- NFS review (mirrors LabImport) ----------------------------------------

interface AnalyteRow {
  key: AnalyteKey;
  customLabel: string;
  unit: AnalyteUnit;
  customUnit: string;
  value: string;
  referenceLow: string;
  referenceHigh: string;
}

const NFS_PRESET: { key: AnalyteKey; unit: AnalyteUnit }[] = [
  { key: 'hemoglobin', unit: 'g/dL' },
  { key: 'wbc', unit: '10e3/uL' },
  { key: 'neutrophils_abs', unit: 'per_mm3' },
  { key: 'platelets', unit: '10e3/uL' },
];

function buildNfsRows(suggestions: NfsSuggestion[]): AnalyteRow[] {
  const byKey = new Map<AnalyteKey, NfsSuggestion>();
  for (const sg of suggestions) if (!byKey.has(sg.key)) byKey.set(sg.key, sg);
  const rows: AnalyteRow[] = NFS_PRESET.map((p) => {
    const hit = byKey.get(p.key);
    return {
      key: p.key,
      customLabel: hit?.customLabel ?? '',
      unit: hit?.unit ?? p.unit,
      customUnit: hit?.customUnit ?? '',
      value: hit?.value ?? '',
      referenceLow: hit?.referenceLow ?? '',
      referenceHigh: hit?.referenceHigh ?? '',
    };
  });
  const presetKeys = new Set(NFS_PRESET.map((p) => p.key));
  for (const sg of suggestions) {
    if (presetKeys.has(sg.key) && sg.key !== 'custom') continue;
    rows.push({
      key: sg.key,
      customLabel: sg.customLabel ?? '',
      unit: sg.unit,
      customUnit: sg.customUnit ?? '',
      value: sg.value,
      referenceLow: sg.referenceLow ?? '',
      referenceHigh: sg.referenceHigh ?? '',
    });
  }
  return rows;
}

function NfsForm({
  submission: s,
  draft,
  tr,
  t,
  onFiled,
}: {
  submission: Submission;
  draft: NfsDraft;
  tr: (m: { fr: string; en: string }) => string;
  t: (key: string) => string;
  onFiled: () => void;
}) {
  const [rows, setRows] = useState<AnalyteRow[]>(() => buildNfsRows(draft.suggestions));
  const [sampleDate, setSampleDate] = useState(draft.sampleDateGuess || today());
  const [resultDate, setResultDate] = useState('');
  const g6pd = draft.baseline?.g6pdStatus ?? null;
  const [applyG6pd, setApplyG6pd] = useState(true);

  function setRow(i: number, patch: Partial<AnalyteRow>) {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function addRow() {
    setRows((rs) => [...rs, { key: 'custom', customLabel: '', unit: 'other', customUnit: '', value: '', referenceLow: '', referenceHigh: '' }]);
  }
  function removeRow(i: number) {
    setRows((rs) => rs.filter((_, idx) => idx !== i));
  }

  const fileM = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ result: { id: string } }>('/labs/results/import', {
        sampleDate,
        resultDate: resultDate || undefined,
        status: 'received',
        isNfsPreset: true,
        sourceAttachmentId: s.attachmentId,
        baseline: applyG6pd && g6pd ? { g6pdStatus: g6pd } : undefined,
        analytes: rows
          .filter((r) => r.value.trim() !== '')
          .map((r) => ({
            key: r.key,
            customLabel: r.key === 'custom' && r.customLabel.trim() ? r.customLabel.trim() : undefined,
            value: r.value.trim(),
            unit: r.unit,
            customUnit: r.unit === 'other' && r.customUnit.trim() ? r.customUnit.trim() : undefined,
            referenceLow: r.referenceLow.trim() || undefined,
            referenceHigh: r.referenceHigh.trim() || undefined,
          })),
      });
      await api.post(`/intake/submissions/${s.id}/reviewed`, { resultType: 'lab', resultId: res.result.id });
      return res;
    },
    onSuccess: onFiled,
  });

  const canSubmit = rows.some((r) => r.value.trim() !== '');

  return (
    <form onSubmit={(e) => { e.preventDefault(); fileM.mutate(); }} className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
        <Field label={tr(L.sampleDate)}>
          <input className={inputClass} type="date" value={sampleDate} onChange={(e) => setSampleDate(e.target.value)} required />
        </Field>
        <Field label={tr(L.resultDate)}>
          <input className={inputClass} type="date" value={resultDate} onChange={(e) => setResultDate(e.target.value)} />
        </Field>
      </div>

      {g6pd && (
        <div className="text-sm rounded-lg px-3 py-2 bg-blue-50 border border-blue-200 text-blue-900">
          <span>
            {tr(L.g6pdDetected)}: <strong>{tr(G6PD_LABEL[g6pd] ?? { fr: g6pd, en: g6pd })}</strong>
          </span>
          <label className="mt-1 flex items-center gap-2 text-blue-800">
            <input type="checkbox" checked={applyG6pd} onChange={(e) => setApplyG6pd(e.target.checked)} />
            {tr(L.applyToRecord)}
          </label>
        </div>
      )}

      <Table headers={[tr(L.analyte), tr(L.value), tr(L.unit), tr(L.refLow), tr(L.refHigh), '']}>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-slate-100">
            <td className="py-1 pr-2 min-w-40">
              <Select value={r.key} onChange={(e) => setRow(i, { key: e.target.value as AnalyteKey })}>
                {ANALYTE_KEYS.map((k) => (
                  <option key={k} value={k}>{t(`analyte.${k}`)}</option>
                ))}
              </Select>
              {r.key === 'custom' && (
                <input
                  className={`${inputClass} mt-1`}
                  placeholder={tr(L.customLabel)}
                  value={r.customLabel}
                  onChange={(e) => setRow(i, { customLabel: e.target.value })}
                />
              )}
            </td>
            <td className="py-1 pr-2">
              <input className={inputClass} inputMode="decimal" value={r.value} onChange={(e) => setRow(i, { value: e.target.value })} />
            </td>
            <td className="py-1 pr-2 min-w-28">
              <Select value={r.unit} onChange={(e) => setRow(i, { unit: e.target.value as AnalyteUnit })}>
                {ANALYTE_UNITS.map((u) => (
                  <option key={u} value={u}>{fmtUnit(u)}</option>
                ))}
              </Select>
              {r.unit === 'other' && (
                <input
                  className={`${inputClass} mt-1`}
                  placeholder={tr(L.customUnit)}
                  value={r.customUnit}
                  onChange={(e) => setRow(i, { customUnit: e.target.value })}
                />
              )}
            </td>
            <td className="py-1 pr-2">
              <input className={inputClass} inputMode="decimal" value={r.referenceLow} onChange={(e) => setRow(i, { referenceLow: e.target.value })} />
            </td>
            <td className="py-1 pr-2">
              <input className={inputClass} inputMode="decimal" value={r.referenceHigh} onChange={(e) => setRow(i, { referenceHigh: e.target.value })} />
            </td>
            <td className="py-1 pr-2 whitespace-nowrap">
              <button type="button" className="text-xs underline text-danger" onClick={() => removeRow(i)}>
                {tr(L.remove)}
              </button>
            </td>
          </tr>
        ))}
      </Table>

      <button type="button" className="text-sm underline text-brand" onClick={addRow}>
        {tr(L.addAnalyte)}
      </button>

      {fileM.isError && <ErrorState error={fileM.error} fallback={t('common.error')} />}

      <Button type="submit" disabled={fileM.isPending || !canSubmit}>
        {tr(L.fileNfs)}
      </Button>
    </form>
  );
}

// --- Serology review (mirrors SerologyImport) ------------------------------

interface MarkerRow {
  marker: SerologyMarker;
  qualitative: string;
  titer: string;
  titerUnit: string;
  interpretation: string;
}

const SEROLOGY_PRESET: SerologyMarker[] = ['igg', 'igm', 'iga'];

function buildMarkerRows(results: SerologySuggestion[]): MarkerRow[] {
  const byMarker = new Map<string, SerologySuggestion>();
  for (const r of results) if (!byMarker.has(r.marker)) byMarker.set(r.marker, r);
  const rows: MarkerRow[] = SEROLOGY_PRESET.map((m) => {
    const hit = byMarker.get(m);
    return {
      marker: m,
      qualitative: hit?.qualitative ?? '',
      titer: hit?.titer ?? '',
      titerUnit: hit?.titerUnit ?? '',
      interpretation: hit?.interpretation ?? '',
    };
  });
  for (const r of results) {
    if ((SEROLOGY_PRESET as string[]).includes(r.marker)) continue;
    rows.push({
      marker: r.marker,
      qualitative: r.qualitative ?? '',
      titer: r.titer ?? '',
      titerUnit: r.titerUnit ?? '',
      interpretation: r.interpretation ?? '',
    });
  }
  return rows;
}

function SerologyForm({
  submission: s,
  draft,
  tr,
  t,
  onFiled,
}: {
  submission: Submission;
  draft: SerologyDraft;
  tr: (m: { fr: string; en: string }) => string;
  t: (key: string) => string;
  onFiled: () => void;
}) {
  const [rows, setRows] = useState<MarkerRow[]>(() => buildMarkerRows(draft.results));
  const [sampleDate, setSampleDate] = useState(draft.sampleDateGuess || today());
  const [resultDate, setResultDate] = useState('');

  function setRow(i: number, patch: Partial<MarkerRow>) {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function addRow() {
    setRows((rs) => [...rs, { marker: 'igg', qualitative: '', titer: '', titerUnit: '', interpretation: '' }]);
  }
  function removeRow(i: number) {
    setRows((rs) => rs.filter((_, idx) => idx !== i));
  }

  const fileM = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ serology: { id: string }[] }>('/serology/results/import', {
        sampleDate,
        resultDate: resultDate || undefined,
        sourceAttachmentId: s.attachmentId,
        results: rows
          .filter((r) => r.qualitative.trim() !== '' || r.titer.trim() !== '')
          .map((r) => ({
            marker: r.marker,
            qualitative: r.qualitative || undefined,
            titer: r.titer.trim() || undefined,
            titerUnit: r.titerUnit.trim() || undefined,
            interpretation: r.interpretation.trim() || undefined,
          })),
      });
      await api.post(`/intake/submissions/${s.id}/reviewed`, { resultType: 'serology', resultId: res.serology[0]?.id });
      return res;
    },
    onSuccess: onFiled,
  });

  const canSubmit = rows.some((r) => r.qualitative.trim() !== '' || r.titer.trim() !== '');

  return (
    <form onSubmit={(e) => { e.preventDefault(); fileM.mutate(); }} className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
        <Field label={tr(L.sampleDate)}>
          <input className={inputClass} type="date" value={sampleDate} onChange={(e) => setSampleDate(e.target.value)} required />
        </Field>
        <Field label={tr(L.resultDate)}>
          <input className={inputClass} type="date" value={resultDate} onChange={(e) => setResultDate(e.target.value)} />
        </Field>
      </div>

      <Table headers={[tr(L.marker), tr(L.qualitative), tr(L.titer), tr(L.titerUnit), tr(L.interpretation), '']}>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-slate-100">
            <td className="py-1 pr-2 min-w-24">
              <Select value={r.marker} onChange={(e) => setRow(i, { marker: e.target.value as SerologyMarker })}>
                {SEROLOGY_MARKERS.map((m) => (
                  <option key={m} value={m}>{t(`serology.${m}`)}</option>
                ))}
              </Select>
            </td>
            <td className="py-1 pr-2 min-w-28">
              <Select value={r.qualitative} onChange={(e) => setRow(i, { qualitative: e.target.value })}>
                <option value="">{tr(L.none)}</option>
                {SEROLOGY_QUALITATIVE.map((qv) => (
                  <option key={qv} value={qv}>{qv}</option>
                ))}
              </Select>
            </td>
            <td className="py-1 pr-2">
              <input className={inputClass} inputMode="decimal" value={r.titer} onChange={(e) => setRow(i, { titer: e.target.value })} />
            </td>
            <td className="py-1 pr-2 min-w-20">
              <input className={inputClass} value={r.titerUnit} onChange={(e) => setRow(i, { titerUnit: e.target.value })} placeholder="UI/mL" />
            </td>
            <td className="py-1 pr-2">
              <input className={inputClass} value={r.interpretation} onChange={(e) => setRow(i, { interpretation: e.target.value })} />
            </td>
            <td className="py-1 pr-2 whitespace-nowrap">
              <button type="button" className="text-xs underline text-danger" onClick={() => removeRow(i)}>
                {tr(L.remove)}
              </button>
            </td>
          </tr>
        ))}
      </Table>

      <button type="button" className="text-sm underline text-brand" onClick={addRow}>
        {tr(L.addMarker)}
      </button>

      {fileM.isError && <ErrorState error={fileM.error} fallback={t('common.error')} />}

      <Button type="submit" disabled={fileM.isPending || !canSubmit}>
        {tr(L.fileSerology)}
      </Button>
    </form>
  );
}

// --- Imaging review (mirrors ImagingImport) --------------------------------

function ImagingForm({
  submission: s,
  draft,
  tr,
  t,
  onFiled,
}: {
  submission: Submission;
  draft: ImagingDraft;
  tr: (m: { fr: string; en: string }) => string;
  t: (key: string) => string;
  onFiled: () => void;
}) {
  const initialExam =
    draft.examType && (EXAM_TYPES as readonly string[]).includes(draft.examType) ? draft.examType : 'etf';
  const [examType, setExamType] = useState<string>(initialExam);
  const [examDate, setExamDate] = useState(draft.examDate || today());
  const [specialist, setSpecialist] = useState(draft.specialist ?? '');
  const [findings, setFindings] = useState(draft.findings ?? '');
  const [status, setStatus] = useState('completed');
  const [nextDate, setNextDate] = useState(draft.nextDate ?? '');

  const fileM = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ appointment: { id: string } }>('/appointments', {
        date: examDate,
        examType,
        specialist: specialist.trim() || undefined,
        findings: findings.trim() || undefined,
        status,
        nextDate: nextDate || undefined,
        sourceAttachmentId: s.attachmentId,
      });
      await api.post(`/intake/submissions/${s.id}/reviewed`, { resultType: 'appointment', resultId: res.appointment.id });
      return res;
    },
    onSuccess: onFiled,
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); fileM.mutate(); }} className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
        <Field label={tr(L.examType)}>
          <Select value={examType} onChange={(e) => setExamType(e.target.value)}>
            {EXAM_TYPES.map((x) => (
              <option key={x} value={x}>{t(`exam.${x}`)}</option>
            ))}
          </Select>
        </Field>
        <Field label={tr(L.examDate)}>
          <input className={inputClass} type="date" value={examDate} onChange={(e) => setExamDate(e.target.value)} required />
        </Field>
        <Field label={tr(L.specialist)}>
          <input className={inputClass} value={specialist} onChange={(e) => setSpecialist(e.target.value)} />
        </Field>
        <Field label={tr(L.status)}>
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            {APPOINTMENT_STATUSES.map((st) => (
              <option key={st} value={st}>{st}</option>
            ))}
          </Select>
        </Field>
      </div>

      <Field label={tr(L.findings)}>
        <Textarea rows={5} value={findings} placeholder={tr(L.findingsPlaceholder)} onChange={(e) => setFindings(e.target.value)} />
      </Field>

      <Field label={tr(L.nextDate)}>
        <input className={inputClass} type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} />
      </Field>

      {fileM.isError && <ErrorState error={fileM.error} fallback={t('common.error')} />}

      <Button type="submit" disabled={fileM.isPending || findings.trim() === ''}>
        {tr(L.fileImaging)}
      </Button>
    </form>
  );
}

// --- Other (unrecognized) --------------------------------------------------

function OtherForm({
  submission: s,
  tr,
  t,
  onFiled,
}: {
  submission: Submission;
  tr: (m: { fr: string; en: string }) => string;
  t: (key: string) => string;
  onFiled: () => void;
}) {
  const reviewedM = useMutation({
    mutationFn: () => api.post(`/intake/submissions/${s.id}/reviewed`, { resultType: 'none', resultId: null }),
    onSuccess: onFiled,
  });
  const dismissM = useMutation({
    mutationFn: () => api.post(`/intake/submissions/${s.id}/dismiss`, {}),
    onSuccess: onFiled,
  });

  return (
    <div className="space-y-3">
      <div className="text-sm rounded-lg px-3 py-2 bg-slate-50 border border-slate-200 text-slate-700">
        {tr(L.otherNotice)}
      </div>
      {(reviewedM.isError || dismissM.isError) && (
        <ErrorState error={reviewedM.error ?? dismissM.error} fallback={t('common.error')} />
      )}
      <div className="flex gap-2">
        <Button type="button" onClick={() => reviewedM.mutate()} disabled={reviewedM.isPending}>
          {tr(L.markReviewed)}
        </Button>
        <Button type="button" variant="secondary" onClick={() => dismissM.mutate()} disabled={dismissM.isPending}>
          {tr(L.dismiss)}
        </Button>
      </div>
    </div>
  );
}

export { L as reportsInboxStrings };
